import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useRegion } from '../context/RegionContext';
import { useInView } from '../hooks/useInView';

const Section = styled.section`
  padding: 120px 0;
  background: #7C3A00;
  position: relative;
  overflow: hidden;
  @media (min-width: 768px) { padding: 160px 0; }
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url('/images/hero-cajeta.jpg');
    background-size: cover;
    background-position: center 40%;
    pointer-events: none;
  }
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(26,18,8,0.55) 0%,
      rgba(124,58,0,0.72) 50%,
      rgba(26,18,8,0.65) 100%
    );
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 840px;
  margin: 0 auto;
  padding: 0 32px;
  text-align: center;
  position: relative;
  z-index: 1;
  transition: opacity 1s ease, transform 1s ease;
  ${({ $inView }) =>
    $inView
      ? css`opacity: 1; transform: translateY(0);`
      : css`opacity: 0; transform: translateY(40px);`}
`;

const Eyebrow = styled.p`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(250,243,224,0.6);
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2.6rem, 6vw, 4.2rem);
  color: #FAF3E0;
  margin-bottom: 24px;
  letter-spacing: -0.01em;
  line-height: 1.04;
`;

const Desc = styled.p`
  font-size: 1.05rem;
  color: rgba(250,243,224,0.72);
  max-width: 540px;
  margin: 0 auto 48px;
  line-height: 1.8;
`;

const Btn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 18px 48px;
  background: #FFFDF7;
  color: #7C3A00;
  font-family: 'Jost', sans-serif;
  font-weight: 500;
  font-size: 0.88rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-radius: 999px;
  transition: background 0.25s, transform 0.2s, box-shadow 0.25s;
  &:hover {
    background: #FAF3E0;
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(26,18,8,0.3);
  }
`;

export function WholesaleCta() {
  const { t } = useLanguage();
  const { region } = useRegion();
  const { ref, isInView } = useInView({ threshold: 0.3 });

  return (
    <Section>
      <Container ref={ref} $inView={isInView}>
        <Eyebrow>{t('Wholesale', 'Mayoreo', 'Vente en Gros')}</Eyebrow>
        <Title>{t('Bring Hacienda Real to Your Store', 'Lleva Hacienda Real a Tu Tienda', 'Portez Hacienda Real dans Votre Boutique')}</Title>
        <Desc>
          {t(
            'We partner with retailers who value quality, authenticity, and heritage. Contact us for wholesale opportunities.',
            'Colaboramos con tiendas que valoran la calidad, autenticidad y herencia. Contáctanos para oportunidades de mayoreo.',
            "Nous collaborons avec des détaillants qui valorisent la qualité, l'authenticité et l'héritage. Contactez-nous pour les opportunités de gros."
          )}
        </Desc>
        <Btn to={`/${region}/contact`}>{t('Contact Us', 'Contáctanos', 'Contactez-nous')}</Btn>
      </Container>
    </Section>
  );
}
